import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Layers as LayersIcon, Upload as UploadIcon } from '@mui/icons-material';
import {
  REFERENCE_LAYER_ACCEPT,
  REFERENCE_LAYER_TYPE_LABELS,
  deleteReferenceLayer,
  formatReferenceLayerApiError,
  geometryKindLabel,
  isSupportedReferenceLayerFile,
  listReferenceLayers,
  updateReferenceLayer,
  type ReferenceLayerDto,
} from '../../services/referenceLayerApi';
import { invalidateReferenceLayers } from '../../hooks/useReferenceLayers';
import ReferenceLayerImportDialog from './ReferenceLayerImportDialog';

/**
 * Admin: upload shared reference layers (boundaries, districts, infrastructure,
 * POIs, routes) from GeoJSON / KML / KMZ / Shapefile zip / CSV, set the default
 * style, toggle visibility, edit or delete. Users see active layers in their
 * map settings and can switch them on per map.
 */
const ReferenceLayersAdminPanel: React.FC = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [layers, setLayers] = useState<ReferenceLayerDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [editing, setEditing] = useState<ReferenceLayerDto | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setLayers(await listReferenceLayers());
    } catch (e) {
      setError(formatReferenceLayerApiError(e, 'Failed to load reference layers'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const f = files[0];
    if (!isSupportedReferenceLayerFile(f.name)) {
      setError(`${f.name}: unsupported type. Use GeoJSON, KML/KMZ, a zipped Shapefile, or CSV.`);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setError(null);
    setSuccess(null);
    setPendingFile(f);
  };

  const onSaved = async (saved: ReferenceLayerDto) => {
    if (inputRef.current) inputRef.current.value = '';
    setPendingFile(null);
    setEditing(null);
    invalidateReferenceLayers();
    await load();
    setSuccess(`Saved "${saved.name}" (${saved.feature_count.toLocaleString()} ${geometryKindLabel(saved.geometry_kind).toLowerCase()}). Users can switch it on in Map settings → Reference layers.`);
  };

  const toggleActive = async (layer: ReferenceLayerDto, active: boolean) => {
    setBusyId(layer.id);
    try {
      const updated = await updateReferenceLayer(layer.id, { active });
      setLayers((prev) => prev.map((l) => (l.id === layer.id ? updated : l)));
      invalidateReferenceLayers();
    } catch (e) {
      setError(formatReferenceLayerApiError(e, 'Update failed'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (layer: ReferenceLayerDto) => {
    if (!window.confirm(`Delete "${layer.name}"? It will disappear from every user's map.`)) return;
    setBusyId(layer.id);
    try {
      await deleteReferenceLayer(layer.id);
      setLayers((prev) => prev.filter((l) => l.id !== layer.id));
      invalidateReferenceLayers();
      setSuccess(`Deleted "${layer.name}".`);
    } catch (e) {
      setError(formatReferenceLayerApiError(e, 'Delete failed'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Reference Layers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Shared boundaries, districts, infrastructure, POIs and routes. Upload GeoJSON, KML/KMZ, a zipped Shapefile
            (.shp + .dbf + .prj) or CSV points. Geometry is kept as-is; you set the default style and every user can
            switch the layer on and tune colours in their own map settings.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<UploadIcon />} onClick={() => inputRef.current?.click()} sx={{ flexShrink: 0 }}>
          Upload layer
        </Button>
        <input ref={inputRef} type="file" hidden accept={REFERENCE_LAYER_ACCEPT} onChange={(e) => handleSelect(e.target.files)} />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!loading && layers.length === 0 && !error && (
        <Alert severity="info">
          <AlertTitle>No reference layers yet</AlertTitle>
          Upload a boundary or district file to get started. Polygons, lines and points are all supported.
        </Alert>
      )}

      <List dense disablePadding>
        {layers.map((layer) => (
          <ListItem
            key={layer.id}
            divider
            sx={{ opacity: layer.active ? 1 : 0.6, pr: 20 }}
            secondaryAction={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Tooltip title={layer.active ? 'Visible to users' : 'Hidden from users'}>
                  <Switch size="small" checked={layer.active} disabled={busyId === layer.id} onChange={(e) => void toggleActive(layer, e.target.checked)} />
                </Tooltip>
                <Tooltip title="Edit name, type, label field, default style">
                  <IconButton size="small" onClick={() => setEditing(layer)} disabled={busyId === layer.id}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => void remove(layer)} disabled={busyId === layer.id}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: layer.geometry_kind === 'point' ? '50%' : '4px',
                  bgcolor: layer.style.color,
                  opacity: 0.7,
                  border: `2px solid ${layer.style.stroke_color}`,
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {layer.name}
                  </Typography>
                  <Chip size="small" label={REFERENCE_LAYER_TYPE_LABELS[layer.layer_type] || layer.layer_type} sx={{ height: 18, fontSize: '0.62rem' }} />
                  <Chip size="small" variant="outlined" label={geometryKindLabel(layer.geometry_kind)} sx={{ height: 18, fontSize: '0.62rem' }} />
                  {layer.style.color_by && (
                    <Chip size="small" variant="outlined" label={`colour by ${layer.style.color_by.field}`} sx={{ height: 18, fontSize: '0.62rem' }} />
                  )}
                </Stack>
              }
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {layer.feature_count.toLocaleString()} features · {layer.source_format || 'unknown format'}
                  {layer.label_field ? ` · label: ${layer.label_field}` : ''}
                  {layer.source ? ` · ${layer.source}` : ''}
                  {layer.uploaded_by ? ` · ${layer.uploaded_by}` : ''} · {new Date(layer.updated_at).toLocaleString()}
                  {layer.description ? ` — ${layer.description}` : ''}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
      {loading && layers.length === 0 && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
          <LayersIcon fontSize="small" color="disabled" />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Stack>
      )}

      <ReferenceLayerImportDialog
        open={Boolean(pendingFile) || Boolean(editing)}
        file={pendingFile}
        layer={editing}
        onClose={() => {
          setPendingFile(null);
          setEditing(null);
          if (inputRef.current) inputRef.current.value = '';
        }}
        onSaved={(saved) => void onSaved(saved)}
      />
    </Box>
  );
};

export default ReferenceLayersAdminPanel;
